'use client'

import { useState } from 'react'
import type { Route } from 'next'
import { useRouter } from 'next/navigation'
import { ChevronRight, ListChecks, Route as RouteIcon } from 'lucide-react'
import { CardCanvas, CanvasPanel } from '@/components/patterns/nested-surface'
import { CardDescription, CardTitle } from '@/components/ui/card'
import { routes } from '@/config/routes'
import {
  ScoreTierBar,
  nextWealthScoreBand,
  wealthScoreBand,
  type PrescriptionStep,
} from '@/features/cases'
import { StepTeaser } from '@/features/investor/components/home/step-teaser'

/**
 * Ported from `DashboardHome.tsx`'s `GrowthPlanCard`.
 *
 * The prescription read as a potential score rather than a checklist. The score the plan can reach
 * is the headline — that number is what the advice is worth to the investor — with today's score
 * and the next band's threshold as the reference points that give it meaning.
 */
export function GrowthPlanCard({
  score,
  steps,
  onStepsClick,
}: {
  score: number
  steps: PrescriptionStep[]
  /** Overrides the default navigation to `/dashboard/audit` — used by the staff-side
   * `ClientDashboardPreview`, where that route doesn't exist for a staff session (it's
   * investor-only and would redirect away). Absent for every real investor caller. */
  onStepsClick?: () => void
}) {
  const router = useRouter()
  const [teaserPaused, setTeaserPaused] = useState(false)
  const nextBand = nextWealthScoreBand(score)
  const stepsToGo = steps.filter((s) => !s.done).length
  // Illustrative, not promised — comfortably past the next band's threshold rather than a specific
  // guaranteed score, and always phrased with "can". In the top band the only headroom left is 100.
  const projected = nextBand ? Math.min(100, score + nextBand.pointsAway + 10) : 100
  const uplift = projected - score
  // Where the next band begins: the line the plan has to clear, drawn green on the bar.
  const threshold = nextBand ? score + nextBand.pointsAway : null
  // The band the projected score lands in — names the destination rather than leaving it a number.
  const projectedBand = wealthScoreBand(projected)

  return (
    <CardCanvas>
      <CanvasPanel className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex flex-row items-center gap-2.5">
          <span className="border-primary/20 bg-primary/10 text-primary flex size-10 shrink-0 items-center justify-center rounded-xl border">
            <RouteIcon className="size-4" />
          </span>
          <div className="min-w-0">
            <CardTitle className="text-muted-foreground text-[11px] font-semibold uppercase tracking-wide">
              Your Growth Plan
            </CardTitle>
            <CardDescription className="text-xs">
              {nextBand ? `Your path to ${nextBand.label}` : 'You are in the top band'}
            </CardDescription>
          </div>
        </div>

        {/* One framed unit: the today → potential jump, then the bar that unit is measured on.
            Deliberately almost wordless — the numbers carry the meaning, so the card is read at a
            glance rather than actually read. */}
        <div className="flex flex-1 flex-col justify-center">
          <div>
            <div className="flex items-end justify-between gap-3">
              <div className="min-w-0">
                <p className="text-muted-foreground text-xs">
                  <span className="text-foreground text-xl font-bold tabular-nums">{score}</span> of
                  100 today
                </p>
                <p className="text-muted-foreground mt-1 text-[10px] font-semibold uppercase tracking-wide">
                  {nextBand ? `${nextBand.pointsAway} pts to ${nextBand.label}` : 'Top band'}
                </p>
              </div>
              <div className="shrink-0 text-right">
                {/* --text-success rather than --chart-positive: the chart green is tuned for fills and
                    drops under 3:1 on a light card, which text still has to clear. */}
                <p
                  className="text-[40px] font-bold tabular-nums leading-none"
                  style={{ color: 'var(--text-success)' }}
                >
                  {projected}
                </p>
                <p
                  className="mt-1 text-[10px] font-semibold uppercase tracking-wide"
                  style={{ color: 'var(--text-success)' }}
                >
                  {uplift > 0 ? 'with your plan' : projectedBand.label}
                </p>
              </div>
            </div>

            {/* The same band-pill bar the prescription page draws, so the investor sees one scale
                across both screens. `reach` adds the second, weaker fill for where the plan lands. */}
            <div
              className="mt-4"
              role="img"
              aria-label={
                threshold != null
                  ? `Wealth Score ${score} out of 100 today, up to ${projected} with your plan. ${nextBand?.label} starts at ${threshold}.`
                  : `Wealth Score ${score} out of 100.`
              }
            >
              <ScoreTierBar score={score} reach={projected} showScale />
            </div>
          </div>
        </div>
      </CanvasPanel>

      {/* Second panel: the advice teaser and the way in to it. Pointer or keyboard anywhere in this
          panel holds the rotation still — the CTA is a real tab stop, so keyboard users get the pause
          without a phantom one being invented. */}
      <CanvasPanel
        className="flex flex-col gap-2 p-3"
        onMouseEnter={() => setTeaserPaused(true)}
        onMouseLeave={() => setTeaserPaused(false)}
        onFocusCapture={() => setTeaserPaused(true)}
        onBlurCapture={() => setTeaserPaused(false)}
      >
        <StepTeaser steps={steps} paused={teaserPaused} />
        <button
          type="button"
          onClick={onStepsClick ?? (() => router.push(routes.investor.auditPrescription as Route))}
          className="border-primary/20 bg-primary/5 hover:bg-primary/10 flex items-center gap-2.5 rounded-[10px] border p-3 text-left transition-colors"
        >
          <span className="bg-primary/10 text-primary flex size-8 shrink-0 items-center justify-center rounded-lg">
            <ListChecks className="size-4" />
          </span>
          <span className="min-w-0 flex-1 text-xs font-semibold">
            {!nextBand
              ? 'Holding the top band'
              : stepsToGo > 0
                ? `${stepsToGo} ${stepsToGo === 1 ? 'step' : 'steps'} to get there`
                : 'Next steps coming soon'}
          </span>
          <ChevronRight className="text-muted-foreground size-4 shrink-0" />
        </button>
      </CanvasPanel>
    </CardCanvas>
  )
}
